import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { createActivityApi, updateActivityApi } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { DURATION_PRESETS } from "@/lib/constants";

const MIN_DURATION = 1;
const MAX_DURATION = 720;

/**
 * Form state, validation and submission for the Add Activity screen.
 * Stage 2.2: also powers the Edit Activity screen — pass `{ activity,
 * initialShared }` to start from an existing activity and save via PUT.
 * Keeps both page components focused purely on rendering.
 */
export const useAddActivityForm = ({ activity = null, initialShared = false } = {}) => {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { t } = useLanguage();

  const isEdit = Boolean(activity);
  const editHasPreset = isEdit && DURATION_PRESETS.includes(activity.duration_minutes);

  const [type, setType] = useState(
    isEdit ? activity.type : profile.preferred_activity || null
  );
  const [duration, setDuration] = useState(editHasPreset ? activity.duration_minutes : 30);
  const [customMode, setCustomMode] = useState(isEdit && !editHasPreset);
  const [customValue, setCustomValue] = useState(
    isEdit && !editHasPreset ? String(activity.duration_minutes) : ""
  );
  const [intensity, setIntensity] = useState(isEdit ? activity.intensity : "Medium");
  const [moodBefore, setMoodBefore] = useState(isEdit ? activity.mood_before : null);
  const [moodAfter, setMoodAfter] = useState(isEdit ? activity.mood_after : null);
  const [note, setNote] = useState(isEdit ? activity.note || "" : "");
  // Privacy-aware: edit starts from the REAL current share state (never
  // auto-shares); create defaults to sharing only for public profiles that
  // have already accepted the community terms (Phase 10 UGC gate).
  const [share, setShare] = useState(
    isEdit
      ? initialShared
      : profile.privacy === "public" && Boolean(profile.ugc_terms_accepted_at)
  );
  const [saving, setSaving] = useState(false);

  const effectiveDuration = customMode ? parseInt(customValue, 10) : duration;

  const validate = () => {
    if (!type) return t("addActivity.chooseTypeError");
    if (
      !effectiveDuration ||
      effectiveDuration < MIN_DURATION ||
      effectiveDuration > MAX_DURATION
    ) {
      return t("addActivity.durationError");
    }
    return null;
  };

  const save = async () => {
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSaving(true);
    const payload = {
      type,
      duration_minutes: effectiveDuration,
      intensity,
      mood_before: moodBefore,
      mood_after: moodAfter,
      note: note.trim(),
      share_to_community: share,
    };
    try {
      if (isEdit) {
        await updateActivityApi(activity.id, profile.id, payload);
        toast.success(t("editActivity.saved"));
        navigate("/progress", { replace: true });
      } else {
        const created = await createActivityApi({ profile_id: profile.id, ...payload });
        navigate("/motion-card", { state: { activity: created }, replace: true });
      }
    } catch (e) {
      // Phase 10 (UGC): server refuses community sharing without accepted terms
      if (e?.response?.status === 403) {
        toast.error(t("ugcTerms.requiredError"));
      } else {
        toast.error(t(isEdit ? "editActivity.saveError" : "addActivity.saveError"));
      }
      setSaving(false);
    }
  };

  return {
    type,
    setType,
    duration,
    setDuration,
    customMode,
    setCustomMode,
    customValue,
    setCustomValue,
    intensity,
    setIntensity,
    moodBefore,
    setMoodBefore,
    moodAfter,
    setMoodAfter,
    note,
    setNote,
    share,
    setShare,
    saving,
    save,
  };
};
